"use client";

// The colour MODEL — HSV↔RGB↔hex, shared by every surface that speaks colour.
//
// This file used to also carry a picker (a circular hue dial + tone/light
// sliders). That was a second colour language beside the arc dial, so it's gone:
// there is ONE instrument now, components/os/ArcPicker (law_one_picker), and it
// composes these functions. Keep this file free of UI.

import type { RGB } from "../lib/organism/color";

// ---- HSV <-> RGB (0..1) + hex ----
export function hsvToRgb(h: number, s: number, v: number): RGB {
  const c = v * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = v - c;
  let r = 0, g = 0, b = 0;
  if (h < 60) [r, g, b] = [c, x, 0];
  else if (h < 120) [r, g, b] = [x, c, 0];
  else if (h < 180) [r, g, b] = [0, c, x];
  else if (h < 240) [r, g, b] = [0, x, c];
  else if (h < 300) [r, g, b] = [x, 0, c];
  else [r, g, b] = [c, 0, x];
  return [r + m, g + m, b + m];
}
export function rgbToHsv(c: RGB): { h: number; s: number; v: number } {
  const r = c[0], g = c[1], b = c[2];
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  const d = max - min;
  let h = 0;
  if (d !== 0) {
    if (max === r) h = 60 * (((g - b) / d) % 6);
    else if (max === g) h = 60 * ((b - r) / d + 2);
    else h = 60 * ((r - g) / d + 4);
  }
  if (h < 0) h += 360;
  return { h, s: max === 0 ? 0 : d / max, v: max };
}
export const rgbToHex = (c: RGB) =>
  "#" + c.map((v) => Math.round(Math.max(0, Math.min(1, v)) * 255).toString(16).padStart(2, "0")).join("");
export function hexToRgb(hex: string): RGB {
  const h = (hex || "").replace("#", "").trim();
  const n = h.length === 3 ? h.split("").map((c) => c + c).join("") : h;
  const int = parseInt((n.slice(0, 6) || "888888"), 16);
  return [((int >> 16) & 255) / 255, ((int >> 8) & 255) / 255, (int & 255) / 255];
}

export const COLOR_LABEL: React.CSSProperties = { fontSize: "0.7rem", letterSpacing: "0.04em", color: "var(--ink-3)" };
